/**
 * SchedulePreview - Shows preview of next scheduled run times
 *
 * Displays upcoming run times based on cron expression with
 * human-readable descriptions and relative time formatting.
 */
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Clock, Calendar, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useSchedulePreview } from '@/hooks/useConnectors';
import { DEFAULT_SCHEDULE_TIMEZONE } from '@/constants/timezone';
import { describeCron } from '@/lib/cron/describe';

interface SchedulePreviewProps {
  connectorType: string;
  connectorName: string;
  cronExpression: string;
  timezone?: string;
  count?: number;
  className?: string;
}

/**
 * Format a Date to 12-hour time with named timezone for preview display.
 *
 * Uses the schedule's configured timezone (not the user's local timezone)
 * so preview times reflect when the job runs on the server.
 */
function formatPreviewDateTime(date: Date, tz: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
    timeZone: tz,
  }).format(date);
}

/**
 * SchedulePreview Component
 */
export const SchedulePreview: React.FC<SchedulePreviewProps> = ({
  connectorType,
  connectorName,
  cronExpression,
  timezone = DEFAULT_SCHEDULE_TIMEZONE,
  count = 5,
  className,
}) => {
  const {
    data: preview,
    isLoading,
    error,
    isFetching,
  } = useSchedulePreview(connectorType, connectorName, cronExpression, timezone, count, !!cronExpression);

  const humanReadable = describeCron(cronExpression, timezone);

  if (!cronExpression || cronExpression.trim().length === 0) {
    return (
      <Card className={cn('', className)}>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground py-4">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Enter a cron expression to see the schedule preview</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={cn('', className)}>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Schedule Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Array.from({ length: count }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cn('', className)}>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Invalid cron expression. Please check the format.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Schedule Preview
          {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
        </CardTitle>
        <CardDescription className="text-xs">
          {humanReadable}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {preview?.next_runs && preview.next_runs.length > 0 ? (
            preview.next_runs.map((runTime, index) => {
              const date = new Date(runTime);
              const isFirst = index === 0;
              return (
                <div
                  key={index}
                  className={cn(
                    'flex items-center justify-between p-2 rounded-md border',
                    isFirst && 'bg-primary/5 border-primary/20'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Calendar className={cn('h-4 w-4', isFirst ? 'text-primary' : 'text-muted-foreground')} />
                    <span className={cn('text-sm', isFirst && 'font-medium')}>
                      {formatPreviewDateTime(date, timezone)}
                    </span>
                  </div>
                  <span className={cn(
                    'text-xs',
                    isFirst ? 'text-primary font-medium' : 'text-muted-foreground'
                  )}>
                    {isFirst ? 'Next run: ' : ''}
                    {formatDistanceToNow(date, { addSuffix: true })}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="text-center text-muted-foreground py-4">
              <p className="text-sm">No upcoming runs scheduled</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SchedulePreview;
